import { get } from "lodash";
import { thor } from "../../typings/thor";

enum AddressComponentType {
    Country = "country",
    Locality = "locality",
    PostCode = "postal_code",
    Route = "route",
    StreetNbr = "street_number",
    PostalTown = "postal_town"
}

export class MapService {
    public static $inject: string[] = [
        "$q",
        "uiGmapGoogleMapApi"
    ];

    constructor (
        private $q: ng.IQService,
        private uiGmapGoogleMapApi
    ) {}

    public isValidAddress(address: thor.SaleAddress): boolean {
        return !!(address.formatted_address &&
            address.postcode &&
            address.city &&
            address.location &&
            address.location.lat &&
            address.location.lon);
    }

    public geocodeAddress(address: string) {
        const deferred = this.$q.defer();

        this.uiGmapGoogleMapApi.then(gmaps => {
            const geocoder = new gmaps.Geocoder();
            geocoder.geocode({ "address": address }, (results, status) => {
                if (status === gmaps.GeocoderStatus.OK) {
                    deferred.resolve({
                        coords: {
                            lat: results[0].geometry.location.lat(),
                            lon: results[0].geometry.location.lng()
                        },
                        geocode: {
                            address: this._parseGeocodeResult(results),
                            status: true
                        }
                    });
                } else {
                    deferred.reject(status);
                }
            });
        });

        return deferred.promise;
    }

    public geocodePosition(position) {
        const deferred = this.$q.defer();

        this.uiGmapGoogleMapApi.then(gmaps => {
            const geocoder = new gmaps.Geocoder();
            geocoder.geocode(
                {
                    location: {
                        lat: position.lat,
                        lng: position.lon
                    },
                    language: "fr"
                },
                (results, status) => {
                    if (status === gmaps.GeocoderStatus.OK) {
                        deferred.resolve(this._parseGeocodeResult(results));
                    } else {
                        deferred.reject(status);
                    }
                });
        });

        return deferred.promise;
    }

    private _parseGeocodeResult(geocodeResult: google.maps.GeocoderResult[]): thor.SaleAddress {
        const address: thor.SaleAddress = {
            postcode: "",
            city: "",
            street: "",
            country: "",
            street_number: "",
            route: "",
            formatted_address: "",
            location: {
                lat: 0,
                lon: 0
            }
        };
        const firstResult = geocodeResult[0];

        address.formatted_address = get(firstResult, "formatted_address");

        if (firstResult.geometry.location) {
            address.location = {
                lat: firstResult.geometry.location.lat(),
                lon: firstResult.geometry.location.lng()
            };
        }

        firstResult.address_components.forEach((addressComponent: google.maps.GeocoderAddressComponent) => {
            const addressComponentType: AddressComponentType = get(addressComponent, "types[0]");
            const addressComponentValue = addressComponentType === AddressComponentType.Country ?
                addressComponent.short_name :
                addressComponent.long_name;

            if (addressComponentValue) {
                switch (addressComponentType) {
                    case AddressComponentType.StreetNbr:
                        address.street = addressComponentValue;
                        address.street_number = addressComponentValue;
                        break;
                    case AddressComponentType.Route:
                        address.street = address.street === "" ?
                            addressComponentValue :
                            `${ address.street } ${ addressComponentValue }`;
                        address.route = addressComponentValue;
                        break;
                    case AddressComponentType.Locality:
                        address.city = addressComponentValue;
                        break;
                    case AddressComponentType.PostalTown:
                        if (!address.city) {
                            address.city = addressComponentValue;
                        }
                        break;
                    case AddressComponentType.Country:
                        address.country = addressComponentValue;
                        break;
                    case AddressComponentType.PostCode:
                        address.postcode = addressComponentValue;
                        break;
                    default:
                    // just ignore other cases
                }
            }
        });

        return address;
    }
}
